





















































































































































































































































import { AxiosError } from 'axios';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, truncate } from 'lodash';
import moment from 'moment';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbar from '@/mixins/snackbar';

import {
  IAttachment,
  IOvertimeRequest,
  IMentionAttachmentComment,
  OvertimeRequestComment,
  OvertimeRequestCommentAttachment,
  OvertimeRequestCommentFieldError
} from '@/models';
import { ErrorManager, ISystemError } from '@/models/error';

import {
  createFile,
  diffDeep,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';

import CommentAttachmentList from '../common/CommentAttachmentList.vue';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';
import QuillEditor from '../common/QuillEditor.vue';
import MentionProvider from '../mention/MentionProvider.vue';

export default mixins(auth, snackbar).extend({
  name: 'OvertimeRequestCommentListItem',

  components: {
    CommentAttachmentList,
    DeleteConfirmationDialog,
    QuillEditor,
    MentionProvider
  },

  props: {
    overtimeRequest: {
      type: Object as PropType<IOvertimeRequest>,
      required: true
    },

    overtimeRequestComment: {
      type: Object as PropType<IMentionAttachmentComment>,
      required: true
    }
  },

  data() {
    return {
      isEditing: false,

      commentLoading: false,

      commentErrorMessage: '',
      commentErrorMessageDetail: '',

      // delete comment
      deleteCommentError: null as ISystemError | null,

      commentDeleteInProgress: false,
      selectedOvertimeRequestComment: cloneDeep(this.overtimeRequestComment),

      deleteCommentDialog: false,

      commentTextHtml: this.overtimeRequestComment.getTextHtml(),
      mentionsLoading: false,

      deleteCommentAttachmentDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IAttachment,
      attachments: [] as IAttachment[]
    };
  },

  computed: {
    ...mapGetters({
      mentionableUsers: 'overtimeRequest/getOvertimeRequestMentionableUsers'
    }),

    isOvertimeRequestCommentOwner(): boolean {
      return this.getCurrentUserFullName === this.overtimeRequestComment.owner;
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedOvertimeRequestComment.text) &&
        !this.commentLoading
      );
    },

    textHasDifference(): boolean {
      return (
        this.selectedOvertimeRequestComment.text ===
        this.overtimeRequestComment.text
      );
    }
  },

  methods: {
    ...mapActions({
      updateOvertimeRequestComment:
        'overtimeRequest/updateOvertimeRequestComment',
      deleteOvertimeRequestComment:
        'overtimeRequest/deleteOvertimeRequestComment',
      addOvertimeRequestCommentAttachments:
        'overtimeRequest/addOvertimeRequestCommentAttachments',
      removeOvertimeRequestCommentAttachment:
        'overtimeRequest/removeOvertimeRequestCommentAttachment',
      fetchOvertimeRequestCommentsMentionableUsers:
        'overtimeRequest/fetchOvertimeRequestCommentsMentionableUsers'
    }),

    truncateFileName(filename: string) {
      const extension = filename.split('.').pop()!;

      return `${truncate(filename, {
        length: 30,
        omission: '[...]'
      })}${extension}`;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new OvertimeRequestCommentAttachment({ file: file })
        )
      );
    },

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    getInitials(name: string) {
      return name
        .toUpperCase()
        .split(' ')
        .map(n => n[0])
        .join('');
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected overtimeRequest comment data to the original when reediting
        this.selectedOvertimeRequestComment = cloneDeep(
          this.overtimeRequestComment
        );
        this.commentTextHtml = this.overtimeRequestComment.getTextHtml();
      }

      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (!this.selectedOvertimeRequestComment.text || this.textHasDifference) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.deleteCommentError = null;
      this.commentErrorMessage = '';
      this.commentErrorMessageDetail = '';
    },

    closeCommentDeleteDialog() {
      this.clearAllErrors();
      this.deleteCommentDialog = false;
    },

    removeOvertimeRequestComment(comment: IMentionAttachmentComment) {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deleteOvertimeRequestComment({
        overtimeId: this.overtimeRequest.id,
        commentId: comment.id
      })
        .then(() => {
          this.closeCommentDeleteDialog();
        })
        .catch(error => {
          this.deleteCommentError = setErrorFromAxiosError(error as AxiosError);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    editComment() {
      this.clearAllErrors();

      this.commentLoading = true;

      const parsedCommentMentionText = OvertimeRequestComment.parseHtmlToMentionText(
        this.commentTextHtml
      );

      this.selectedOvertimeRequestComment.text = parsedCommentMentionText;

      const commentDelta = diffDeep(
        this.selectedOvertimeRequestComment,
        this.overtimeRequestComment,
        true
      );

      return this.updateOvertimeRequestComment({
        overtimeId: this.overtimeRequest.id,
        comment: commentDelta
      })
        .then(async () => {
          if (this.attachments.length !== 0) {
            await this.addOvertimeRequestCommentAttachments({
              overtimeId: this.overtimeRequest.id,
              commentId: commentDelta.id,
              attachments: this.attachments
            });
            this.attachments = [];
          }
          this.selectedOvertimeRequestComment = cloneDeep(
            this.overtimeRequestComment
          );
          this.isEditing = false;
        })
        .catch(error => {
          if (error.response) {
            // client received an error response that falls out of range 2xx
            if (error.response.data.detail) {
              this.commentErrorMessageDetail = error.response.data.detail;
            }
          }
          this.commentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentLoading = false;
        });
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchOvertimeRequestCommentsMentionableUsers(
          this.overtimeRequestComment.id
        );
      } catch (error) {
        this.commentErrorMessage = setErrorFromAxiosError(
          error as AxiosError
        ).message;
      } finally {
        this.mentionsLoading = false;
      }
    },

    downloadAttachmentFile(attachment: IAttachment) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    },

    openAttachmentDeleteDialog(attachment: IAttachment) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.$nextTick(() => {
        this.deleteCommentAttachmentDialog = true;
      });
    },

    closeCommentAttachmentDeleteDialog() {
      this.deleteCommentAttachmentDialog = false;
    },

    removeCommentAttachment(attachment: IAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeOvertimeRequestCommentAttachment({
        overtimeId: this.overtimeRequest.id,
        commentId: this.overtimeRequestComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.deleteCommentAttachmentDialog = false;
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    }
  }
});
